import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Spinner, ErrorLocal } from 'components';
import { useMutation, useQuery } from '@apollo/client';
import { useQuizContext, useQuizSessionContext } from 'contexts';
import { getJeopardyQuizConfig } from 'graphql/queries';
import { insertSessionProgress, upsertJeopardyGameState } from 'graphql/mutations';
import { JeopardyProvider, useJeopardyContext } from './jeopardy-context';
import { JeopardyGameOverview } from './jeopardy-game-overview';
import { JeopardyLobby } from './jeopardy-lobby';
import { getJeopardyThemeVars } from './jeopardy-theme';
import './jeopardy-quiz.css';
import {
    JeopardyBoardConfig,
    JeopardyGameState,
    JeopardySessionSettings,
    DEFAULT_JEOPARDY_GAME_STATE,
} from './jeopardy-types';

/**
 * Quiz master control view for Jeopardy sessions.
 * Wraps the lobby (pre-game team setup) and the master game console.
 * Accessed at the /overview route for Jeopardy quiz sessions.
 */
export const JeopardyOverview = () => {
    const { quiz } = useQuizContext();
    const sessionContext = useQuizSessionContext();
    const theme = useTheme();
    const jeopardyThemeVars = React.useMemo(() => getJeopardyThemeVars(theme), [theme]);

    const sessionId = sessionContext?.session?.id;
    const sessionSettings: JeopardySessionSettings | null = sessionContext?.session?.quizSettings ?? null;

    const { data, loading, error } = useQuery(getJeopardyQuizConfig, {
        variables: { quizId: quiz.id },
        skip: !quiz.id,
    });

    if (loading) return <Spinner />;
    if (error) return <ErrorLocal errorMessage="Error loading Jeopardy board." errorDetails={error} />;
    if (!sessionId) return <ErrorLocal errorMessage="No active session found." />;

    const configRows = data?.quiz_master_quiz_settings ?? [];
    const boardConfig: JeopardyBoardConfig | null = configRows[0]?.additional_config ?? null;

    return (
        <div style={{ ...jeopardyThemeVars, height: '100%' }}>
            <JeopardyProvider sessionId={sessionId}>
                <JeopardyOverviewInner
                    boardConfig={boardConfig}
                    sessionId={sessionId}
                    initialSessionSettings={sessionSettings}
                />
            </JeopardyProvider>
        </div>
    );
};

interface InnerProps {
    boardConfig: JeopardyBoardConfig | null;
    sessionId: number;
    initialSessionSettings: JeopardySessionSettings | null;
}

/**
 * Inner component with access to JeopardyContext.
 * Shows the lobby when game hasn't started; transitions to master console once started.
 */
const JeopardyOverviewInner = ({ boardConfig, sessionId, initialSessionSettings }: InnerProps) => {
    const { gameState, sessionProgressId, loading } = useJeopardyContext();
    const { quiz } = useQuizContext();

    const [insertProgress] = useMutation(insertSessionProgress);
    const [upsertState] = useMutation(upsertJeopardyGameState);

    if (loading) return <Spinner />;

    // If no session progress row exists yet, the game hasn't started — show lobby
    const gameStarted = sessionProgressId !== null && gameState.phase !== 'lobby';

    const titleBar = (
        <div className="jeopardy-overview__title-bar">
            <span className="jeopardy-overview__quiz-name">{quiz.name}</span>
            <span className={`jeopardy-overview__state-badge ${gameStarted ? '--in-progress' : '--lobby'}`}>
                {gameStarted ? '● In Progress' : '● Lobby'}
            </span>
        </div>
    );

    const handleStartGame = async (settings: JeopardySessionSettings) => {
        const initialState: JeopardyGameState = {
            ...DEFAULT_JEOPARDY_GAME_STATE,
            phase: 'single_jeopardy',
            teams: settings.teams,
            remainingSeconds: settings.timerSeconds,
        };

        if (!sessionProgressId) {
            // Create session_progress row and initialize state
            const result = await insertProgress({
                variables: {
                    sessionId,
                    isPaused: false,
                    progressIndex: 0,
                },
            });
            const newProgressId = result.data?.insert_quiz_master_session_progress_one?.id;
            if (newProgressId) {
                await upsertState({
                    variables: {
                        sessionProgressId: newProgressId,
                        additionalInfo: initialState,
                    },
                });
            }
        } else {
            // Progress row exists - just update the state (resume scenario)
            await upsertState({
                variables: {
                    sessionProgressId,
                    additionalInfo: initialState,
                },
            });
        }
    };

    if (!gameStarted) {
        return (
            <div className="jeopardy-overview__shell">
                {titleBar}
                <div className="jeopardy-overview__shell-content">
                    <JeopardyLobby
                        boardConfig={boardConfig}
                        onStartGame={handleStartGame}
                    />
                </div>
            </div>
        );
    }

    if (!boardConfig) {
        return (
            <div className="jeopardy-overview__shell">
                {titleBar}
                <div className="jeopardy-overview__shell-content">
                    <ErrorLocal errorMessage="Jeopardy board configuration is missing. Configure the board in quiz settings before starting a session." />
                </div>
            </div>
        );
    }

    const timerSeconds = gameState.remainingSeconds > 0 ? gameState.remainingSeconds : 15;

    return (
        <div className="jeopardy-overview__shell">
            {titleBar}
            <div className="jeopardy-overview__shell-content">
                <JeopardyGameOverview
                    boardConfig={boardConfig}
                    timerSeconds={timerSeconds}
                />
            </div>
        </div>
    );
};
